import React, { useState } from 'react';

interface NewspaperPropProps {
  onClick: () => void;
  hasNews: boolean;
}

/**
 * A clickable coffee table with a newspaper on it.
 * Replaces the plain coffee table div in LobbyReception.
 */
export const NewspaperProp: React.FC<NewspaperPropProps> = ({ onClick, hasNews }) => {
  const [isHovered, setIsHovered] = useState(false);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick();
    }
  };

  return (
    <div
      className="flex flex-col items-center mb-1 cursor-pointer group"
      role="button"
      aria-label="Read newspaper"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Coffee table surface */}
      <div
        className={`relative w-16 h-8 rounded-sm bg-theme-bg-secondary border border-theme-border
          transition-all duration-200
          ${isHovered ? 'shadow-md scale-105' : 'shadow-sm'}`}
      >
        {/* Newspaper on the table */}
        <div
          className={`absolute top-1 left-1/2 -translate-x-1/2 w-10 h-5 rounded-[1px]
            bg-amber-50 border border-amber-200/60 overflow-hidden
            transition-all duration-200
            ${isHovered ? 'shadow-sm brightness-105' : ''}`}
        >
          {/* Masthead line */}
          <div className="h-[3px] mx-1 mt-[2px] bg-amber-900/30 rounded-[0.5px]" />
          {/* Column lines */}
          <div className="flex gap-[1px] px-[2px] mt-[2px]">
            <div className="flex-1 space-y-[1px]">
              <div className="h-[1px] bg-amber-900/15" />
              <div className="h-[1px] bg-amber-900/10" />
              <div className="h-[1px] bg-amber-900/10" />
            </div>
            <div className="w-[0.5px] bg-amber-900/10" />
            <div className="flex-1 space-y-[1px]">
              <div className="h-[1px] bg-amber-900/15" />
              <div className="h-[1px] bg-amber-900/10" />
              <div className="h-[1px] bg-amber-900/10" />
            </div>
          </div>

          {/* News dot indicator */}
          {hasNews && (
            <div className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-theme-accent border border-white/80" />
          )}
        </div>

        {/* "Read" tooltip on hover */}
        {isHovered && (
          <div className="absolute -top-6 left-1/2 -translate-x-1/2 px-1.5 py-0.5
            text-[8px] font-medium tracking-wider uppercase
            bg-theme-bg-primary text-theme-text-secondary
            border border-theme-border rounded shadow-sm whitespace-nowrap">
            Read
          </div>
        )}
      </div>

      {/* Table legs */}
      <div className="flex justify-between w-14 mt-0.5">
        <div className="w-1 h-1.5 bg-theme-text-muted/15 rounded-b" />
        <div className="w-1 h-1.5 bg-theme-text-muted/15 rounded-b" />
      </div>
    </div>
  );
};

export default NewspaperProp;
